import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    HostBinding,
    Input,
    NgZone,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
} from '@angular/core';
import { Cms } from '@impact/data';
import SwiperCore, { Autoplay, EffectFade, Keyboard, Navigation } from 'swiper/core';
import { AutoplayOptions } from 'swiper/types';

import { FeatureDetectionService } from '../utils/helpers/feature-detection.service';

export interface  MediaSliderItem {
    media?: Cms.Media;
    videoFallbackImage?: Cms.Media;
    vimeoVideoID?: string;
    richText?: string;
    alignTextLeft?: boolean;
}

SwiperCore.use([Autoplay, Navigation, EffectFade, Keyboard]);
@Component({
    selector: 'app-media-slider',
    template: `
        <button *ngIf="showNavigation" class="media-slider-close" type="button" (click)="closed.emit()">
            ${'<svg #closeCircle class="svg-close-circle svg-icon" viewBox="0 0 58 58"><g fill="none" fill-rule="evenodd"><circle cx="29" cy="29" r="29" fill="currentColor"/><path fill="#FFF" d="M34.47 22.47l1.06 1.06L30.061 29l5.469 5.47-1.06 1.06L29 30.061l-5.47 5.469-1.06-1.06L27.939 29l-5.469-5.47 1.06-1.06L29 27.939z"/></g></svg>'}
        </button>
        <!-- https://swiperjs.com/angular -->
        <swiper
            *ngIf="isBrowser && sliderItems && sliderItems.length > 1; else singleMedia"
            effect="fade"
            class="media-slider-content swiper-container"
            [preloadImages]="true"
            [keyboard]="{
                enabled: true
            }"
            (slideChange)="onSlideChange($event)"
            [index]="activeIndex"
            [autoplay]="setAutoPlay"
            [navigation]="showNavigation"
            [loop]="true"
        >
            <ng-template swiperSlide *ngFor="let item of sliderItems; index as i">
                <app-component-loader></app-component-loader>
                <app-media-item *ngIf="item.media" [item]="item.media"></app-media-item>
            </ng-template>
        </swiper>
        <ng-template #singleMedia>
            <div class="media-slider__single-image">
                <app-media-item *ngIf="sliderItems && sliderItems.length > 0 && sliderItems[0].media" [item]="sliderItems[0].media"></app-media-item>
            </div>
        </ng-template>
        <div class="media-slider-counter">
            <span class="counter" *ngIf="!hideCounter">{{ activeIndex + 1 }} / {{ sliderItems.length }}</span>
            &nbsp;
            <span *ngIf="showPhotoName && sliderItems[activeIndex] && sliderItems[activeIndex].media">
                    {{ sliderItems[activeIndex].media?.alt }}
            </span>
        </div>

        <app-rich-text
            class="media-slider-text"
            [class.media-slider-text--left]="sliderItems[activeIndex].alignTextLeft"
            *ngIf="sliderItems[activeIndex] && sliderItems[activeIndex].richText"
            [html]="sliderItems[activeIndex].richText"
        ></app-rich-text>

        <div class="media-slider-thumbs swiper-container">
            <div class="media-slider-thumbs__wrapper swiper-wrapper">
                <swiper *ngIf="isBrowser && innerShowThumbs" [simulateTouch]="false">
                    <ng-template swiperSlide class="media-slider-thumbs-slide-wrap " *ngFor="let mediaItem of sliderItems; index as i">
                        <div class="media-slider-thumbs-slide" [class.swiper-slide-thumb-active]="activeIndex === i">
                            <button class="media-slider-thumbs-slide__button" (click)="setActiveIndex(i)">
                                <img
                                    *ngIf="mediaItem.media?.type === 'Image'; else videoThumb"
                                    class="media-slider-thumbs__thumb"
                                    [attr.src]="mediaItem.media?.src | safe: 'resourceUrl'"
                                    [attr.alt]="mediaItem.media?.alt"
                                    loading="lazy"
                                />
                                <ng-template #videoThumb>
                                    <i aria-hidden="true" class="media-slider-thumbs__thumb"
                                        >${'<svg #playIcon class="svg-playIcon svg-icon" viewBox="0 0 30 30"><path stroke="currentColor" d="M20 15l-8 5V10z" fill="none" fill-rule="evenodd"/></svg>'}</i
                                    >
                                </ng-template>
                            </button>
                        </div>
                    </ng-template>
                </swiper>
            </div>
        </div>
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MediaSliderComponent implements OnInit, OnChanges {
    @HostBinding('class') class = 'media-slider';
    @Input() sliderItems: MediaSliderItem[] = [];
    @Input() showNavigation = false;
    @Input() hideCounter = false;
    @Input() showThumbs = true;
    @Input() showPhotoName = false;
    @Input() autoPlay = true;
    @Input() autoPlayDelay = 10000;
    @Input() startIndex?: number;
    @Output() closed = new EventEmitter();
    @Output() indexChange = new EventEmitter<number>();
    activeIndex: number;
    innerShowThumbs = false;
    autoPlayOptions: AutoplayOptions = {
        delay: this.autoPlayDelay,
        disableOnInteraction: true,
    };
    isBrowser: boolean;
    setAutoPlay: boolean | AutoplayOptions = false;

    constructor(
        private featureDetectionService: FeatureDetectionService,
        private zone: NgZone,
        private cdr: ChangeDetectorRef
    ) {
        this.isBrowser = this.featureDetectionService.isBrowser();
    }

    ngOnInit() {
        // Set optional configuration objects for Swiper. Must be done in ngOnInit()
        this.setAutoPlay = this.autoPlay ? this.autoPlayOptions : false;
        this.activeIndex = this.startIndex ?? 0;

        if (this.showThumbs && this.sliderItems.length > 1) {
            this.innerShowThumbs = true;
            // Check if thumbs are visible. If yes add a component css-class that adds some aditional space for content
            this.class = 'media-slider media-slider-content--visible-thumbs';
        }
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.sliderItems && !changes.sliderItems.firstChange) {
            this.setActiveIndex(0);
            this.cdr.markForCheck();
        }
    }

    onSlideChange(swiper: any) {
        // Looks like this event handler runs outside angular zone which
        // means changes won't be detected as usual.
        // I consider this a bug in swiper...
        // The following will force it to be run inside angular zone.
        this.zone.run(() => {
            this.setActiveIndex(swiper.realIndex);
            this.indexChange.emit(swiper.realIndex);
        });
    }

    setActiveIndex(index: number) {
        this.activeIndex = index;
    }
}
